import type { DomainOption } from '../types';

/** State map shared with App.tsx — mirrors mapMcpResult() in
 *  server/routes/ud.js. */
export type UdResult =
  | { state: 'register';      domain: string; priceUsd: number | null }
  | { state: 'aftermarket';   domain: string; priceUsd: number | null; isOffer?: boolean; source: 'ud' | 'afternic' | 'sedo' | 'external'; buyUrl: string }
  | { state: 'taken-broker';  domain: string; buyUrl: string }
  | { state: 'backorder';     domain: string; priceUsd?: number | null }
  | { state: 'unknown';       domain?: string };

export type AvailabilityMap = Record<string, UdResult>;

interface Props {
  options: DomainOption[];
  selectedNumber: number | null;
  onSelect: (number: number) => void;
  /** Per-domain availability + state populated by the UD bulk-check
   *  proxy. SMN deep-link mode leaves this null because SMN renders
   *  its own results in its own UI. */
  availability?: AvailabilityMap | null;
  /** Click handler for the pill — App owns it because some states
   *  (register) require a backend call before navigating. */
  onPillClick?: (result: UdResult) => void;
}

function fmtUsd(n: number | null | undefined): string {
  if (typeof n !== 'number' || !isFinite(n)) return '';
  return n >= 100
    ? '$' + Math.round(n).toLocaleString()
    : '$' + n.toFixed(2);
}

function pillFor(result: UdResult): {
  cls: string;
  label: string;
  title: string;
  clickable: boolean;
} | null {
  switch (result.state) {
    case 'register': {
      const price = fmtUsd(result.priceUsd);
      return {
        cls: 'result__pill result__pill--register',
        label: price ? `Register ${price}` : 'Register',
        title: 'Add to your Unstoppable Domains cart and open checkout',
        clickable: true,
      };
    }
    case 'aftermarket': {
      const price = fmtUsd(result.priceUsd);
      // For offer-only listings (no BIN, just a minOfferAmount floor)
      // surface the number as "from $X" so the user knows it's a
      // negotiate-up floor, not a one-click buy price. No source
      // suffix — the user is already heading to Unstoppable Domains
      // when they click, the marketplace identity is implicit.
      const priceLabel = price
        ? result.isOffer
          ? `from ${price}`
          : price
        : '';
      return {
        cls: 'result__pill result__pill--aftermarket',
        label: priceLabel ? `Aftermarket ${priceLabel}` : 'Aftermarket',
        title: result.isOffer
          ? 'Open the listing at Unstoppable Domains — offers only, no Buy It Now'
          : 'Open the listing at Unstoppable Domains',
        clickable: true,
      };
    }
    case 'taken-broker':
      return {
        cls: 'result__pill result__pill--broker',
        label: 'Taken · Broker',
        title: 'Registered but not listed — use UD’s broker',
        clickable: true,
      };
    case 'backorder': {
      const price = fmtUsd(result.priceUsd);
      return {
        cls: 'result__pill result__pill--backorder',
        label: price ? `Backorder ${price} fee` : 'Backorder',
        title: 'Pending-delete name — open UD’s backorder marketplace',
        clickable: true,
      };
    }
    case 'unknown':
    default:
      return null;
  }
}

function normalize(value: string): string {
  const v = value.trim().toLowerCase();
  return v.includes('.') ? v : `${v}.com`;
}

export function ResultsList({
  options,
  selectedNumber,
  onSelect,
  availability,
  onPillClick,
}: Props) {
  if (options.length === 0) return null;

  return (
    <ol className="results">
      {options.map((opt) => {
        const selected = opt.number === selectedNumber;
        const key = normalize(opt.domain);
        const result = availability?.[key];
        const pill = result ? pillFor(result) : null;
        return (
          <li
            key={opt.number}
            className={`result${selected ? ' result--selected' : ''}`}
          >
            <button
              type="button"
              className="result__button"
              onClick={() => onSelect(opt.number)}
              aria-pressed={selected}
            >
              <span className="result__number">{opt.number}</span>
              <span className="result__body">
                <span className="result__domain">{opt.domain}</span>
                <span className="result__reason">{opt.reason}</span>
              </span>
            </button>
            {pill && result && (
              <button
                type="button"
                className={pill.cls}
                onClick={(e) => {
                  e.stopPropagation();
                  if (pill.clickable && onPillClick) onPillClick(result);
                }}
                title={pill.title}
                aria-label={pill.label}
              >
                {pill.label}
              </button>
            )}
          </li>
        );
      })}
    </ol>
  );
}
